import axios, { AxiosInstance, AxiosError } from 'axios';
import { 
  AuthResponse, 
  LoginRequest, 
  User, 
  UserCreate, 
  UserUpdate,
  Tenant,
  TenantCreate,
  TenantUpdate,
  TenantList,
  TenantFilters,
  TenantDeployment,
  TenantDeploymentList,
  DeploymentFilters,
  ApiError,

  SizeCreate,
  SizeUpdate,
  SizeListResponse,
  SizeResponse,
  SizeParametersUpdateRequest
} from '../types';
import {
  PlanListResponse,
  Subscription,
  CheckoutCreateRequest,
  CheckoutCreateResponse,
  SubscriptionCancelRequest
} from '../types/billing';
import {
  AccountCreatedResponse,
  AccountPlanRequest,
  ClientAccountCreate,
  ClientAccountDetail,
  ClientAccountSummary,
  ClientAccountUpdate,
  CreditTopUpRequest,
  EmployeeAssignRequest,
  EmployeeStorageRequest,
  EmployeeStorageResponse,
  PublicListingOption
} from '../types/clientAccount';
import {
  CreditPackage,
  CreditPackageCreate,
  CreditPackageUpdate
} from '../types/creditPackage';
import {
  LimitRequestDecisionRequest,
  LimitRequestDecisionResponse,
  ResellerCreate,
  ResellerCreatedResponse,
  ResellerDetail,
  ResellerLimitRequest,
  ResellerMonthlyStatement,
  ResellerPatch,
  ResellerSummary,
  WholesaleTopUpRequest
} from '../types/reseller';
import { PortalAccess } from '../types/portalAccess';

class ApiService {
  private api: AxiosInstance;
  private authToken: string | null = null;

  constructor() {
    // Ensure we use the same protocol as the current page
    const baseURL = window.location.protocol === 'https:' 
      ? `https://${window.location.host}/api/v1`
      : '/api/v1';
      
    this.api = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Load token from localStorage
    this.authToken = localStorage.getItem('auth_token');
    if (this.authToken) {
      this.setAuthToken(this.authToken);
    }

    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        console.error('API Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => {
        console.log(`API Response: ${response.status} ${response.config.url}`);
        return response;
      },
      (error: AxiosError) => {
        console.error('API Response Error:', error);
        
        // Handle 401 errors by clearing auth and redirecting to login
        if (error.response?.status === 401) {
          this.clearAuth();
          window.location.href = '/login';
        }
        
        return Promise.reject(this.formatError(error));
      }
    );
  }

  private setAuthToken(token: string) {
    this.authToken = token;
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('auth_token', token);
  }

  private clearAuth() {
    this.authToken = null;
    delete this.api.defaults.headers.common['Authorization'];
    localStorage.removeItem('auth_token');
  }

  private formatError(error: AxiosError): ApiError {
    if (error.response?.data) {
      return error.response.data as ApiError;
    }
    return {
      detail: error.message || 'An unknown error occurred'
    };
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await this.api.post<AuthResponse>('/auth/login', credentials);
    this.setAuthToken(response.data.access_token);
    return response.data;
  }

  async register(userData: UserCreate): Promise<User> {
    const response = await this.api.post<User>('/auth/register', userData);
    return response.data;
  }

  logout() {
    this.clearAuth();
  }

  // User endpoints
  async getCurrentUser(): Promise<User> {
    const response = await this.api.get<User>('/users/me');
    return response.data;
  }

  async getUsers(skip: number = 0, limit: number = 100): Promise<User[]> {
    const response = await this.api.get<User[]>('/users/', {
      params: { skip, limit }
    });
    return response.data;
  }

  async getUser(userId: number): Promise<User> {
    const response = await this.api.get<User>(`/users/${userId}`);
    return response.data;
  }

  async updateUser(userId: number, userData: UserUpdate): Promise<User> {
    const response = await this.api.put<User>(`/users/${userId}`, userData);
    return response.data;
  }

  async deleteUser(userId: number): Promise<void> {
    await this.api.delete(`/users/${userId}`);
  }

  // Tenant endpoints
  async getTenants(filters: TenantFilters = {}): Promise<TenantList> {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('skip', String((filters.page - 1) * (filters.per_page || 50)));
    if (filters.per_page) params.append('limit', String(filters.per_page));
    if (filters.status) params.append('status_filter', filters.status);

    const response = await this.api.get<TenantList>('/tenants/', { params });
    return response.data;
  }

  async getTenant(tenantId: number): Promise<Tenant> {
    const response = await this.api.get<Tenant>(`/tenants/${tenantId}`);
    return response.data;
  }

  async createTenant(tenantData: TenantCreate): Promise<Tenant> {
    const response = await this.api.post<Tenant>('/tenants/', tenantData);
    return response.data;
  }

  async updateTenant(tenantId: number, tenantData: TenantUpdate): Promise<Tenant> {
    const response = await this.api.put<Tenant>(`/tenants/${tenantId}`, tenantData);
    return response.data;
  }

  async deleteTenant(tenantId: number): Promise<void> {
    await this.api.delete(`/tenants/${tenantId}`);
  }

  async upgradeTenant(tenantId: number): Promise<TenantDeployment> {
    const response = await this.api.post<TenantDeployment>(`/tenants/${tenantId}/upgrade`);
    return response.data;
  }

  async forceDeleteTenant(tenantId: number): Promise<void> {
    await this.api.delete(`/tenants/${tenantId}/force`);
  }

  async cleanTenant(tenantId: number): Promise<void> {
    await this.api.delete(`/tenants/${tenantId}/clean`);
  }

  async syncTenantStatus(tenantId: number): Promise<{ message: string; deployment_status: string; tenant_status: string }> {
    const response = await this.api.post(`/tenants/${tenantId}/sync`);
    return response.data;
  }

  async refreshTenantVersion(tenantId: number): Promise<Tenant> {
    const response = await this.api.post<Tenant>(`/tenants/${tenantId}/refresh-version`);
    return response.data;
  }

  async refreshAllTenantVersions(): Promise<{ updated: number; unavailable: number; total: number }> {
    const response = await this.api.post('/tenants/refresh-versions');
    return response.data;
  }

  async cleanupTenantHistory(tenantId: number, keepLast: number = 10): Promise<{ message: string; kept_recent: number }> {
    const response = await this.api.delete(`/tenants/${tenantId}/history`, {
      params: { keep_last: keepLast }
    });
    return response.data;
  }

  async createEmployeePortalInviteCode(tenantId: number): Promise<{ invite_code: string }> {
    const response = await this.api.post(`/tenants/${tenantId}/employee-portal-invite-code`);
    return response.data;
  }

  // Deployment endpoints
  async getTenantDeployments(tenantId: number, filters: DeploymentFilters = {}): Promise<TenantDeploymentList> {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('skip', String((filters.page - 1) * (filters.per_page || 50)));
    if (filters.per_page) params.append('limit', String(filters.per_page));

    const response = await this.api.get<TenantDeploymentList>(`/deployments/tenant/${tenantId}`, { params });
    return response.data;
  }

  async getAllDeployments(filters: DeploymentFilters = {}): Promise<TenantDeploymentList> {
    const params = new URLSearchParams();
    
    if (filters.page) params.append('skip', String((filters.page - 1) * (filters.per_page || 50)));
    if (filters.per_page) params.append('limit', String(filters.per_page));
    if (filters.status) params.append('status_filter', filters.status);
    if (filters.operation) params.append('operation_filter', filters.operation);

    const response = await this.api.get<TenantDeploymentList>('/deployments/', { params });
    return response.data;
  }

  async getDeployment(deploymentId: number): Promise<TenantDeployment> {
    const response = await this.api.get<TenantDeployment>(`/deployments/${deploymentId}`);
    return response.data;
  }

  async syncDeployment(deploymentId: number): Promise<TenantDeployment> {
    const response = await this.api.post<TenantDeployment>(`/deployments/${deploymentId}/sync`);
    return response.data;
  }

  async getDeploymentLogs(deploymentId: number): Promise<{
    deployment_id: number;
    job_name: string;
    operation: string;
    status: string;
    logs: string;
    started_at: string;
    completed_at?: string;
  }> {
    // Add timestamp to prevent caching
    const timestamp = new Date().getTime();
    const response = await this.api.get(`/deployments/${deploymentId}/logs?_t=${timestamp}`);
    return response.data;
  }

  // Customer endpoints
  async getCustomers(skip: number = 0, limit: number = 50, search?: string, country?: string): Promise<any> {
    const params: any = { skip, limit };
    if (search) params.search = search;
    if (country) params.country = country;

    const response = await this.api.get('/customers/', { params });
    return response.data;
  }

  async getCustomer(id: number): Promise<any> {
    const response = await this.api.get(`/customers/${id}`);
    return response.data;
  }

  async createCustomer(customerData: any): Promise<any> {
    const response = await this.api.post('/customers/', customerData);
    return response.data;
  }

  async updateCustomer(id: number, customerData: any): Promise<any> {
    const response = await this.api.put(`/customers/${id}`, customerData);
    return response.data;
  }

  async deleteCustomer(id: number): Promise<void> {
    await this.api.delete(`/customers/${id}`);
  }

  async getCustomerOptions(): Promise<any[]> {
    const response = await this.api.get('/customers/options');
    return response.data;
  }

  async getCustomersSummary(): Promise<any> {
    const response = await this.api.get('/customers/summary');
    return response.data;
  }

  // Size endpoints
  async getSizes(includeInactive: boolean = false): Promise<SizeListResponse> {
    const params = includeInactive ? { include_inactive: true } : {};
    const response = await this.api.get<SizeListResponse>('/sizes/', { params });
    return response.data;
  }

  async getActiveSizes(): Promise<SizeListResponse> {
    const response = await this.api.get<SizeListResponse>('/sizes/active');
    return response.data;
  }

  async getSize(sizeId: number): Promise<SizeResponse> {
    const response = await this.api.get<SizeResponse>(`/sizes/${sizeId}`);
    return response.data;
  }

  async createSize(sizeData: SizeCreate): Promise<SizeResponse> {
    const response = await this.api.post<SizeResponse>('/sizes/', sizeData);
    return response.data;
  }

  async updateSize(sizeId: number, sizeData: SizeUpdate): Promise<SizeResponse> {
    const response = await this.api.put<SizeResponse>(`/sizes/${sizeId}`, sizeData);
    return response.data;
  }

  async updateSizeParameters(sizeId: number, parametersData: SizeParametersUpdateRequest): Promise<SizeResponse> {
    const response = await this.api.put<SizeResponse>(`/sizes/${sizeId}/parameters`, parametersData);
    return response.data;
  }

  async deleteSize(sizeId: number): Promise<void> {
    await this.api.delete(`/sizes/${sizeId}`);
  }

  // Billing endpoints
  async getBillingConfig(): Promise<{ paddle_client_token: string; paddle_environment: string }> {
    const response = await this.api.get<{ paddle_client_token: string; paddle_environment: string }>('/billing/config');
    return response.data;
  }

  async getPlans(activeOnly: boolean = true): Promise<PlanListResponse> {
    const response = await this.api.get<PlanListResponse>('/billing/plans', {
      params: { active_only: activeOnly }
    });
    return response.data;
  }

  async getMySubscription(): Promise<Subscription> {
    const response = await this.api.get<Subscription>('/billing/subscription/me');
    return response.data;
  }

  async createCheckout(request: CheckoutCreateRequest): Promise<CheckoutCreateResponse> {
    const response = await this.api.post<CheckoutCreateResponse>('/billing/checkout', request);
    return response.data;
  }

  async cancelSubscription(request: SubscriptionCancelRequest): Promise<any> {
    const response = await this.api.post('/billing/subscription/cancel', request);
    return response.data;
  }

  async getSubscriptionHistory(limit: number = 10): Promise<Subscription[]> {
    const response = await this.api.get<Subscription[]>('/billing/subscription/history', {
      params: { limit }
    });
    return response.data;
  }

  // Client account endpoints (cuentas de cliente — empleados IA individuales)
  async createClientAccount(data: ClientAccountCreate): Promise<AccountCreatedResponse> {
    const response = await this.api.post<AccountCreatedResponse>('/client-accounts', data);
    return response.data;
  }

  // Listado de cuentas. Filtros OPCIONALES resueltos en el backend (no en
  // memoria, para que funcionen con muchas cuentas): `managed` (direct|partner|
  // all) y `reseller_id` (un partner concreto). Sin filtros = todas, como antes.
  async getClientAccounts(filters: {
    managed?: 'all' | 'direct' | 'partner';
    reseller_id?: number;
  } = {}): Promise<ClientAccountSummary[]> {
    const params: Record<string, string | number> = {};
    if (filters.managed && filters.managed !== 'all') params.managed = filters.managed;
    if (typeof filters.reseller_id === 'number') params.reseller_id = filters.reseller_id;
    const response = await this.api.get<ClientAccountSummary[]>('/client-accounts', { params });
    return response.data;
  }

  async getClientAccount(accountId: number): Promise<ClientAccountDetail> {
    const response = await this.api.get<ClientAccountDetail>(`/client-accounts/${accountId}`);
    return response.data;
  }

  // PATCH parcial (name/notes/max_employees). No dependemos de la forma de la
  // respuesta: el llamador recarga el detalle tras el cambio.
  async updateClientAccount(accountId: number, data: ClientAccountUpdate): Promise<void> {
    await this.api.patch(`/client-accounts/${accountId}`, data);
  }

  // POST /client-accounts/{id}/plan — asigna/cambia el plan mensual (créditos/mes
  // + día de ciclo 1..28). Separado del topup de «créditos extra». No dependemos
  // de la forma de la respuesta: el llamador recarga el detalle tras el cambio.
  async setAccountPlan(accountId: number, data: AccountPlanRequest): Promise<void> {
    await this.api.post(`/client-accounts/${accountId}/plan`, data);
  }

  async addClientAccountCredits(accountId: number, data: CreditTopUpRequest): Promise<{ entry_id: number; balance: number | string }> {
    const response = await this.api.post(`/client-accounts/${accountId}/credits`, data);
    return response.data;
  }

  async assignClientAccountEmployee(accountId: number, data: EmployeeAssignRequest): Promise<{ account_employee_id: number; status: string }> {
    const response = await this.api.post(`/client-accounts/${accountId}/employees`, data);
    return response.data;
  }

  // POST /client-accounts/{id}/employees/{empId}/storage — AMPLÍA los volúmenes
  // de un empleado (valores TOTALES). Solo aumenta: el backend rechaza con 409
  // cualquier bajada, tamaño fuera de tramo o techo superado. La respuesta trae
  // `sync` con lo que pasó con el DISCO REAL (applied/deferred/failed): un 2xx
  // NO significa que el volumen haya crecido, así que hay que mirarlo.
  async expandClientAccountEmployeeStorage(
    accountId: number,
    employeeId: number,
    data: EmployeeStorageRequest
  ): Promise<EmployeeStorageResponse> {
    const response = await this.api.post(
      `/client-accounts/${accountId}/employees/${employeeId}/storage`, data);
    return response.data;
  }

  async retryClientAccountEmployee(accountId: number, employeeId: number): Promise<{ account_employee_id: number; status: string }> {
    const response = await this.api.post(`/client-accounts/${accountId}/employees/${employeeId}/retry`);
    return response.data;
  }

  async syncClientAccountConsumption(accountId: number): Promise<{ entry_written: boolean; balance: number | string }> {
    const response = await this.api.post(`/client-accounts/${accountId}/sync-consumption`);
    return response.data;
  }

  // Borrado FÍSICO de la cuenta (irreversible, ledger incluido). Sin force el
  // backend responde 409 si quedan empleados sin retirar; con force los retira
  // del tenant (best-effort: los fallos vuelven en remove_failures) y además
  // deshace allí el resto de lo aprovisionado: los usuarios de plataforma
  // (users_cleanup — `kept` son los que SIGUEN VIVOS y por qué) y el proyecto
  // de la cuenta (project_deleted / project_error, donde un 409 significa que
  // al proyecto aún le quedan apps). Esos pasos NO bloquean el borrado local:
  // llegan en la respuesta para avisar al admin de qué quedó vivo en el tenant.
  async deleteClientAccount(
    accountId: number,
    force: boolean
  ): Promise<{
    deleted: boolean;
    employees_removed: number;
    remove_failures: { account_employee_id: number; agent_app_id: number; error: string }[];
    owner_deleted: boolean;
    owner_kept_reason?: string | null;
    users_cleanup?: {
      rows: { email: string; unlinked: boolean; deleted: boolean; kept_reason: string | null }[];
      deleted: number;
      kept: { email: string; reason: string }[];
      error?: string | null;
    };
    project_id?: number | null;
    project_deleted?: boolean;
    project_error?: string | null;
  }> {
    const response = await this.api.delete(`/client-accounts/${accountId}`, {
      params: { force }
    });
    return response.data;
  }

  // Reset de contraseña del EndUser propietario de la cuenta. La generada se
  // devuelve UNA sola vez (mismo contrato que el alta); la anterior deja de valer.
  // email_sent: true solo si además salió por correo (envío fail-soft en backend).
  async resetClientAccountPassword(accountId: number): Promise<{ email: string; generated_password: string; email_sent?: boolean }> {
    const response = await this.api.post<{ email: string; generated_password: string; email_sent?: boolean }>(
      `/client-accounts/${accountId}/reset-password`
    );
    return response.data;
  }

  // Desbloqueo del propietario tras agotar los intentos de login (contrato F1).
  // A diferencia del reset NO toca la contraseña: el cliente sigue con la suya,
  // solo recupera el acceso sin esperar a que expire la ventana de bloqueo.
  async unlockClientAccountOwner(accountId: number): Promise<PortalAccess> {
    const response = await this.api.post<PortalAccess>(`/client-accounts/${accountId}/unlock`);
    return response.data;
  }

  // Partners/resellers (administración — /resellers/*, mismo token de admin)
  async createReseller(data: ResellerCreate): Promise<ResellerCreatedResponse> {
    const response = await this.api.post<ResellerCreatedResponse>('/resellers', data);
    return response.data;
  }

  async getResellers(): Promise<ResellerSummary[]> {
    const response = await this.api.get<ResellerSummary[]>('/resellers');
    return response.data;
  }

  async getReseller(resellerId: number): Promise<ResellerDetail> {
    const response = await this.api.get<ResellerDetail>(`/resellers/${resellerId}`);
    return response.data;
  }

  // PATCH parcial (cupos, descuento, notas). El llamador recarga el detalle.
  async updateReseller(resellerId: number, data: ResellerPatch): Promise<ResellerSummary> {
    const response = await this.api.patch<ResellerSummary>(`/resellers/${resellerId}`, data);
    return response.data;
  }

  // Recarga mayorista (purchase, respeta el techo) o corrección (adjustment).
  async addResellerCredits(
    resellerId: number,
    data: WholesaleTopUpRequest
  ): Promise<{ entry_id: number; wholesale_balance: number | string }> {
    const response = await this.api.post(`/resellers/${resellerId}/credits`, data);
    return response.data;
  }

  // Borrado FÍSICO del partner (irreversible, ledger mayorista incluido). Sin
  // force el backend responde 409 si tiene cartera; con force sus clientes se
  // desvinculan y pasan a cuentas directas del marketplace.
  async deleteReseller(
    resellerId: number,
    force: boolean
  ): Promise<{ deleted: boolean; unlinked_clients: number }> {
    const response = await this.api.delete(`/resellers/${resellerId}`, {
      params: { force }
    });
    return response.data;
  }

  // Reset de contraseña de un usuario del portal partner. Sin email se resetea
  // el PRIMER usuario del reseller (el del alta) — también es la vía de
  // recuperación mientras no exista CRUD de usuarios de partner. La generada
  // se devuelve UNA sola vez; la anterior deja de valer.
  // email_sent: true solo si además salió por correo (envío fail-soft en backend).
  async resetResellerPassword(resellerId: number, email?: string): Promise<{ email: string; generated_password: string; email_sent?: boolean }> {
    const response = await this.api.post<{ email: string; generated_password: string; email_sent?: boolean }>(
      `/resellers/${resellerId}/reset-password`,
      email ? { email } : {}
    );
    return response.data;
  }

  // Desbloqueo de un usuario del portal partner (contrato F1). Mismo selector
  // que el reset: sin email, el PRIMER usuario del reseller. No toca la
  // contraseña.
  async unlockResellerUser(resellerId: number, email?: string): Promise<PortalAccess> {
    const response = await this.api.post<PortalAccess>(
      `/resellers/${resellerId}/unlock`,
      email ? { email } : {}
    );
    return response.data;
  }

  async suspendReseller(resellerId: number): Promise<ResellerSummary> {
    const response = await this.api.post<ResellerSummary>(`/resellers/${resellerId}/suspend`);
    return response.data;
  }

  async activateReseller(resellerId: number): Promise<ResellerSummary> {
    const response = await this.api.post<ResellerSummary>(`/resellers/${resellerId}/activate`);
    return response.data;
  }

  async getResellerStatement(
    resellerId: number,
    year: number,
    month: number
  ): Promise<ResellerMonthlyStatement> {
    const response = await this.api.get<ResellerMonthlyStatement>(
      `/resellers/${resellerId}/statement`,
      { params: { year, month } }
    );
    return response.data;
  }

  // ── Solicitudes de AMPLIACIÓN DEL TECHO mayorista (bandeja del admin) ────
  // OJO al modelo: el partner COMPRA créditos él mismo mientras quepan en su
  // techo (su línea de crédito). Lo que se aprueba aquí es SUBIR esa línea —
  // aprobar NO mueve saldo ni escribe en el ledger: sube max_wholesale_credits.

  // Sin `status` devuelve todas, PENDIENTES primero (una bandeja se ordena por
  // lo que hay que hacer). `resellerId` acota a un partner.
  async getResellerLimitRequests(
    status?: string,
    resellerId?: number
  ): Promise<ResellerLimitRequest[]> {
    const response = await this.api.get<ResellerLimitRequest[]>('/reseller-limit-requests', {
      params: {
        ...(status ? { status } : {}),
        ...(resellerId ? { reseller_id: resellerId } : {})
      }
    });
    return response.data;
  }

  // `max_credits` concede un techo distinto del pedido (subida parcial); sin
  // él, el pedido. 409 si la solicitud ya estaba resuelta.
  async approveResellerLimitRequest(
    requestId: number,
    data: LimitRequestDecisionRequest = {}
  ): Promise<LimitRequestDecisionResponse> {
    const response = await this.api.post<LimitRequestDecisionResponse>(
      `/reseller-limit-requests/${requestId}/approve`, data);
    return response.data;
  }

  async rejectResellerLimitRequest(
    requestId: number,
    data: LimitRequestDecisionRequest = {}
  ): Promise<LimitRequestDecisionResponse> {
    const response = await this.api.post<LimitRequestDecisionResponse>(
      `/reseller-limit-requests/${requestId}/reject`, data);
    return response.data;
  }

  // ── Catálogo de PAQUETES DE CRÉDITOS (producto «empleados IA») ────────────
  // Nada que ver con getPlans(), que es la tabla `plans` (suscripciones B2C de
  // tenants por Paddle). Aquí el admin edita precios y el «para qué vale» de
  // cada paquete sin tocar código.

  // activeOnly=false por defecto: el admin tiene que ver los retirados también.
  async getCreditPackages(activeOnly: boolean = false): Promise<CreditPackage[]> {
    const response = await this.api.get<CreditPackage[]>('/credit-packages', {
      params: { active_only: activeOnly }
    });
    return response.data;
  }

  async createCreditPackage(data: CreditPackageCreate): Promise<CreditPackage> {
    const response = await this.api.post<CreditPackage>('/credit-packages', data);
    return response.data;
  }

  async updateCreditPackage(packageId: number, data: CreditPackageUpdate): Promise<CreditPackage> {
    const response = await this.api.patch<CreditPackage>(`/credit-packages/${packageId}`, data);
    return response.data;
  }

  // Borrado FÍSICO (para limpiar errores de alta). Para RETIRAR un paquete ya
  // ofrecido, la vía es is_active=false: la fila es el registro de su precio.
  async deleteCreditPackage(packageId: number): Promise<void> {
    await this.api.delete(`/credit-packages/${packageId}`);
  }

  // Catálogo público de empleados IA — para el picker por NOMBRE (rellena el slug)
  async getPublicListingOptions(): Promise<PublicListingOption[]> {
    const response = await this.api.get<{ items: PublicListingOption[] }>('/public/agent-employees', {
      params: { limit: 100 }
    });
    return response.data.items || [];
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: number }> {
    const response = await this.api.get('/health');
    return response.data;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.authToken;
  }
}

export const apiService = new ApiService();
export default apiService;